import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';

function UserEmailGenerator() {
  const { token } = useContext(AuthContext);
  const userId = localStorage.getItem('id') || '';
  
  // Verify token on component mount
  useEffect(() => {
    if (!token) {
      setError('Authentication token not found. Please log in again.');
    }
  }, [token]);
  const [leads, setLeads] = useState([]);
  const [emails, setEmails] = useState([]);
  const [email, setEmail] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({
    leadId: '',
    category: '',
    Description: ''
  });
  const [activeTab, setActiveTab] = useState('generate');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch leads
        const leadsResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/leads/assigned/${userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const fetchedLeads = leadsResponse.data || [];
        setLeads(fetchedLeads);
        if (fetchedLeads.length > 0) {
          setFormData(prev => ({ ...prev, leadId: fetchedLeads[0]._id }));
        }

        // Fetch emails
        const emailsResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/email-generator/emails`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setEmails(emailsResponse.data.data || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch data');
      }
    };
    fetchData();
  }, [token, userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Find selected lead
    const selectedLead = leads.find(lead => lead._id === formData.leadId);
    
    if (!selectedLead) {
      setError('Please select a valid lead');
      setLoading(false);
      return;
    }

    try {
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Prepare data according to schema requirements
      const emailData = {
        // leadId: formData.leadId,
        category: formData.category,
        descrption: formData.Description,
        leadEmail: selectedLead.email
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/email-generator/generate`,
        emailData,
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
          } 
        }
      );
      
      setEmail(response.data.email);
      setEmails(prev => [response.data.email, ...prev]);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate email');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEmail = async (emailId, isDraft = false) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/email-generator/emails/${emailId}`,
        { status: isDraft ? 'draft' : 'saved' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmails(prev => prev.map(email => 
        email._id === emailId ? response.data.email : email
      ));
      if (!isDraft) {
        setEmail(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save email');
    }
  };

  const handleEditEmail = async (emailId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/email-generator/emails/${emailId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingEmail(response.data.email);
      setShowEditModal(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch email');
    }
  };

  const handleUpdateEmail = async (emailId, updatedData) => {
    try {
      // Prepare update data with required fields
      const updatePayload = {
        subject: updatedData.subject,
        body: updatedData.body,
        category: updatedData.category,
        status: updatedData.status
      };

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/email-generator/emails/${emailId}`,
        updatePayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setEmails(prev => prev.map(email => 
        email._id === emailId ? response.data.email : email
      ));
      setShowEditModal(false);
      setEditingEmail(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update email');
    }
  };

  const handleDeleteEmail = async (emailId) => {
    if (!window.confirm('Are you sure you want to delete this email?')) return;
    
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/email-generator/emails/${emailId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmails(prev => prev.filter(email => email._id !== emailId));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete email');
    }
  };

  const filteredEmails = emails.filter(email => 
    activeTab === 'generate' ? false : 
    activeTab === 'drafts' ? email.status === 'draft' : 
    email.status === 'saved'
  );

  return (
    <div className="container mx-auto p-2 sm:p-4 lg:p-6 max-w-7xl">
      {/* Tab Navigation */}
      <div className="mb-4 sm:mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex flex-col sm:flex-row" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('generate')}
              className={`w-full sm:w-1/3 py-3 sm:py-4 px-1 text-center border-b-2 font-medium text-xs sm:text-sm transition-colors
                ${activeTab === 'generate'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Generate Email
            </button>
            <button
              onClick={() => setActiveTab('drafts')}
              className={`w-full sm:w-1/3 py-3 sm:py-4 px-1 text-center border-b-2 font-medium text-xs sm:text-sm transition-colors
                ${activeTab === 'drafts'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Drafts ({emails.filter(e => e.status === 'draft').length})
            </button>
            <button
              onClick={() => setActiveTab('saved')}
              className={`w-full sm:w-1/3 py-3 sm:py-4 px-1 text-center border-b-2 font-medium text-xs sm:text-sm transition-colors
                ${activeTab === 'saved'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Saved Emails ({emails.filter(e => e.status === 'saved').length})
            </button>
          </nav>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-xs sm:text-sm">{error}</p>
          <button 
            onClick={() => setError('')}
            className="mt-2 text-xs text-red-700 underline hover:text-red-800"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Generate Email Tab */}
      {activeTab === 'generate' && (
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          <div>
            <label className="block text-gray-700 text-xs sm:text-sm font-bold mb-2">
              Select Lead <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.leadId}
              onChange={(e) => setFormData({ ...formData, leadId: e.target.value })}
              className="shadow border rounded w-full py-2 px-3 text-gray-700 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select Lead</option>
              {leads.map((lead) => (
                <option key={lead._id} value={lead._id}>
                  {lead.name} - {lead.email}
                </option>
              ))}
            </select>
            {formData.leadId && (
              <p className="mt-1 text-xs text-gray-500">
                Email will be generated for: {leads.find(l => l._id === formData.leadId)?.email}
              </p>
            )}
          </div>

          <div>
            <label className="block text-gray-700 text-xs sm:text-sm font-bold mb-2">
              Email Category <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="shadow border rounded w-full py-2 px-3 text-gray-700 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select Category</option>
              <option value="welcome">Welcome</option>
              <option value="followup">Follow-up</option>
              <option value="reminder">Reminder</option>
              <option value="status_update">Status Update</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-700 text-xs sm:text-sm font-bold mb-2">
              Additional Context
            </label>
            <textarea
              value={formData.Description}
              onChange={(e) =>
                setFormData({ ...formData, Description: e.target.value })
              }
              className="shadow border rounded w-full py-2 px-3 text-gray-700 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="4"
              placeholder="Add any specific details or context for the email..."
            ></textarea>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="submit"
              className="w-full sm:w-auto bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
              disabled={loading || !formData.leadId || !formData.category}
            >
              {loading ? 'Generating...' : 'Generate Email'}
            </button>
          </div>

          {/* Generated Email Preview */}
          {email && (
            <div className="mt-4 sm:mt-6 p-4 sm:p-6 border border-gray-200 rounded-lg bg-white shadow-sm">
              <h3 className="font-bold text-base sm:text-lg mb-3 sm:mb-4 text-gray-800">Generated Email</h3>
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <strong className="text-xs sm:text-sm text-gray-600">To:</strong>
                  <p className="mt-1 text-sm sm:text-base text-gray-900">{email.leadEmail}</p>
                </div>
                <div>
                  <strong className="text-xs sm:text-sm text-gray-600">Category:</strong>
                  <p className="mt-1 text-sm sm:text-base text-gray-900 capitalize">{email.category}</p>
                </div>
                <div>
                  <strong className="text-xs sm:text-sm text-gray-600">Subject:</strong>
                  <p className="mt-1 text-sm sm:text-base text-gray-900">{email.subject}</p>
                </div>
                <div>
                  <strong className="text-xs sm:text-sm text-gray-600">Body:</strong>
                  <p className="mt-1 whitespace-pre-wrap text-sm sm:text-base text-gray-900">{email.body}</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 pt-3 sm:pt-4 border-t border-gray-200">
                  <button
                    onClick={() => handleSaveEmail(email._id, true)}
                    className="w-full sm:w-auto bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded transition-colors text-sm sm:text-base"
                  >
                    Save as Draft
                  </button>
                  <button
                    onClick={() => handleSaveEmail(email._id)}
                    className="w-full sm:w-auto bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded transition-colors text-sm sm:text-base"
                  >
                    Save Email
                  </button>
                </div>
              </div>
            </div>
          )}
        </form>
      )}

      {/* Drafts and Saved Emails Tabs */}
      {(activeTab === 'drafts' || activeTab === 'saved') && (
        <div className="space-y-4">
          {filteredEmails.length === 0 ? (
            <div className="text-center py-8 sm:py-12 text-gray-500">
              <p className="text-sm sm:text-base">No {activeTab === 'drafts' ? 'draft' : 'saved'} emails found.</p>
            </div>
          ) : (
            filteredEmails.map(email => (
              <div key={email._id} className="p-4 sm:p-6 border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow">
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2 mb-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                      {email.category}
                    </span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {new Date(email.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div>
                    <strong className="text-xs sm:text-sm text-gray-600">To:</strong>
                    <p className="mt-1 text-sm sm:text-base text-gray-900">{email.leadEmail}</p>
                  </div>
                  <div>
                    <strong className="text-xs sm:text-sm text-gray-600">Subject:</strong>
                    <p className="mt-1 text-sm sm:text-base text-gray-900">{email.subject}</p>
                  </div>
                  <div>
                    <strong className="text-xs sm:text-sm text-gray-600">Body:</strong>
                    <p className="mt-1 whitespace-pre-wrap text-sm sm:text-base text-gray-900 line-clamp-3">{email.body}</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 pt-3 border-t border-gray-200">
                    <button
                      onClick={() => handleEditEmail(email._id)}
                      className="w-full sm:w-auto bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition-colors text-sm sm:text-base"
                    >
                      Edit
                    </button>
                    {activeTab === 'drafts' && (
                      <button
                        onClick={() => handleSaveEmail(email._id)}
                        className="w-full sm:w-auto bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded transition-colors text-sm sm:text-base"
                      >
                        Save as Final
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteEmail(email._id)}
                      className="w-full sm:w-auto bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded transition-colors text-sm sm:text-base"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingEmail && (
        <div 
          className="fixed inset-0 bg-gray-900 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowEditModal(false);
          }}
        >
          <div className="relative w-full max-w-3xl mx-auto bg-white rounded-lg shadow-xl">
            <div className="p-4 sm:p-6 lg:p-8">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">Edit Email</h3>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingEmail(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <label className="block text-gray-700 text-xs sm:text-sm font-bold mb-2">
                    To:
                  </label>
                  <input
                    type="email"
                    value={editingEmail.leadEmail}
                    disabled
                    className="shadow border rounded w-full py-2 px-3 text-gray-500 bg-gray-100 text-sm sm:text-base cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-xs sm:text-sm font-bold mb-2">
                    Category:
                  </label>
                  <select
                    value={editingEmail.category}
                    onChange={(e) => setEditingEmail({
                      ...editingEmail,
                      category: e.target.value
                    })}
                    className="shadow border rounded w-full py-2 px-3 text-gray-700 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="welcome">Welcome</option>
                    <option value="followup">Follow-up</option>
                    <option value="reminder">Reminder</option>
                    <option value="status_update">Status Update</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 text-xs sm:text-sm font-bold mb-2">
                    Subject: <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editingEmail.subject}
                    onChange={(e) => setEditingEmail({
                      ...editingEmail,
                      subject: e.target.value
                    })}
                    className="shadow border rounded w-full py-2 px-3 text-gray-700 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-xs sm:text-sm font-bold mb-2">
                    Body: <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={editingEmail.body}
                    onChange={(e) => setEditingEmail({
                      ...editingEmail,
                      body: e.target.value
                    })}
                    className="shadow border rounded w-full py-2 px-3 text-gray-700 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="10"
                    required
                  />
                </div>
                <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingEmail(null);
                    }}
                    className="w-full sm:w-auto bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-6 rounded transition-colors text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleUpdateEmail(editingEmail._id, editingEmail)}
                    className="w-full sm:w-auto bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded transition-colors text-sm sm:text-base"
                    disabled={!editingEmail.subject || !editingEmail.body}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserEmailGenerator;
